
/**
 * ---------------------
 * Copyright
 * Spinview Global 2020
 * ---------------------
 */

import lodashMerge from 'lodash/merge';

import {assertDependencies, isInsideIFrame} from '../../lib/util';

const MANIFEST = require('./manifest');

const mailTemplate = require('./mail_template');

const RECEIVER = 'cafe_factory';

let play_again = 'Play again';

class CafeFactoryEvent {
	constructor(data) {
		data = data || {};
		this.receiver = data.receiver;
		this.event = data.event;
		this.section = data.section;
		this.target = data.target;
		this.arg = data.arg;
	}
}

const DEFAULT_OPTIONS = {
	first_mission_first_pano: null,
	second_mission_first_pano: null,
	second_mission_last_pano: null,
	// black_screen_text: 'Please wait, the game will start again soon.'
	black_screen_text: ''
};

/**
 * Initialize Data Object we are going to send to
 * SIMSTORE at the end of our tour
*/

let CAFE_FACTORY_TRACKING_DATA = {
	purchased_products: {},
	timer: '',
	user: {},
};

/**
 * Defining EVENTS used in this and additional scripts
 * These events describe events happening inside EVENT SECTIONS (e.g. Product Card)
 * And are used to recognize some actions between different iframe windows
*/

const INTERNAL_EVENTS = {	// Events for the main window
	ping: 'ping',
	leave: 'leave',
	end_simulation: 'end_simulation'
};

const MISSION_EVENTS = {	// Events for the Mission Popup
	mission_start: 'mission_start',
	mission_stop: 'mission_stop',
	next_mission: 'next_mission',
	previous_mission: 'previous_mission',
	open_card: 'open_card'
};

const CART_EVENTS = {	// Events for the Product Cart Popup
	open_cart: 'open_cart',
	remove_from_cart: 'remove_from_cart',
	checkout_cart: 'checkout_cart',
	open_help: 'open_help'
};

const PRODUCT_EVENTS = {	// Events for the Product Cards Popup
	open_product: 'open_product',
	send_product_data: 'send_product_data',
	close_product: 'close_product',
	buy_product: 'buy_product'
};

const OVERLAY_EVENTS = {	// Events for Overlay Popups
	show_overlay: 'show_overlay',
	hide_overlay: 'hide_overlay',
	make_visible: 'make_visible',
	make_hidden: 'make_hidden',
	overlay_click: 'overlay_click',
	// start_timer: 'start_timer',
	// stop_timer: 'stop_timer',
	// send_ticket_data: 'send_ticket_data',
	// elapsed_time: 'elapsed_time'
};

/**
 * Defining SECTIONS THAT CAN FIRE EVENTS
 * Mapping WebsiteViewer IDs to EVENT SECTIONS
 *
 * Every event section controls one part of tour interface (e.g. product cards)
 * It's important that WebsiteViewer IDs mapped to Event Sections in this script,
 * match those in the tour itself
*/

const EVENT_SECTIONS = {
	cart: 'cart',
	product: 'product',
	cart_btn: 'cart_btn',
	// help_btn: 'help_btn',
	exit_btn: 'exit_btn',
	// section_timer:'timer',
};

const OVERLAY_SECTIONS = [
	EVENT_SECTIONS.cart_btn,
	// EVENT_SECTIONS.help_btn,
	EVENT_SECTIONS.exit_btn,
	// EVENT_SECTIONS.section_timer
];

const SECTION_TO_WEBVIEW = {
	[EVENT_SECTIONS.cart]: 'websiteViewer4',
	[EVENT_SECTIONS.product]: 'websiteViewerResp',
	[EVENT_SECTIONS.cart_btn]: 'websiteViewer3',
	// [EVENT_SECTIONS.help_btn]: 'websiteViewer3',
	[EVENT_SECTIONS.exit_btn]: 'websiteViewer1',
	// [EVENT_SECTIONS.section_timer]: 'websiteViewer6',
};

/**
 * Defining CUSTOM EVENTS used in Audience Tracker
 * These events are sent to Audience Tracker along with
 * data they represent, and are saved in our database
*/

const CUSTOM_EVENT_TYPES = {
	cafe_factory_mission_start: 'cafe_factory_mission_start',
	cafe_factory_mission_stop: 'cafe_factory_mission_stop',

	cafe_factory_product_open: 'cafe_factory_product_open',
	cafe_factory_product_close: 'cafe_factory_product_close',
	cafe_factory_product_buy: 'cafe_factory_product_buy',

	cafe_factory_product_delete_from_cart: 'cafe_factory_product_delete_from_cart',

	cafe_factory_product_checkout: 'cafe_factory_product_checkout',

	cafe_factory_leave: 'cafe_factory_leave',

	cafe_factory_elapsed_time: 'cafe_factory_elapsed_time',
};

/**
 * MAIN LOGIC
 *
 * This Is our main application logic that controls everything from
 * event dispatching and reciving to showing and hiding UI Elements
 *
 * UI manipulation of the UI ELEMENTS (EVENT SECTIONS) themself is done from
 * inside their internal scripts.
 */

function CafeFactory(options = DEFAULT_OPTIONS, deps = MANIFEST.dependencies) {

	const thisCafeFactoryStore = this;

	options = lodashMerge({}, DEFAULT_OPTIONS, options);
	assertDependencies(MANIFEST.name, MANIFEST.dependencies, deps);

	const log = deps.logger.child(MANIFEST.name);
    warnMissingOptions(options);

    //-------------------------------------------------------------

	let activeProduct = null;
	let activeMission = null;
	let currentPage = null;
	let _locationInterval = setInterval(checkLocation, 500);

	Object.assign(thisCafeFactoryStore, /** @lends CafeFactory.prototype */ {
		init,

		// Example: { receiver: RECIVER, event: INTERNAL_EVENTS.event, target: 'Some Target', section: EVENT_SECTIONS.section };
		sendEvent
	});

	function init() {
		deps.window.addEventListener('message', handleMessage);
		log.verbose('Initialized');

		// Drop user back to first panorama in the tour at tour init
		deps.krPanoClient.goToScene(options.first_mission_first_pano);

		currentPage = window.location.href;

		hideScroll(OVERLAY_SECTIONS);
	}

	function handleMessage(e) {
		const evt = new CafeFactoryEvent(e.data);
		if (evt.receiver !== RECEIVER) {
			return;
		}

		sendEvent(evt);
	}

	/**
	 * Respond to recived events by sending out an event
	 * Control the data manipulation from here also
	 */

	function sendEvent(evt) {

		// Keep idle timer alive

		if (evt.event === INTERNAL_EVENTS.ping) {
			deps.idleDetector.ping();
			return;
		}

		/**
	 	 * Direct leave event - Probably sent by GFK
	 	 * don't send any data if this event is called
	 	 */

		if (evt.event === INTERNAL_EVENTS.leave) {
			deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_leave);
			return endSimulation();
		}

		// Any EVENT SECTION can send an OVERLAY EVENT

		if (evt.event === OVERLAY_EVENTS.hide_overlay || evt.event === OVERLAY_EVENTS.show_overlay || evt.event === OVERLAY_EVENTS.make_visible) {
			OVERLAY_SECTIONS.forEach(section => {
				sendEventToIFrame(evt.event, section);
			});
			return;
		}

		// if (evt.event === OVERLAY_EVENTS.start_timer || evt.event === OVERLAY_EVENTS.stop_timer) {
		// 		sendEventToIFrame(evt.event, EVENT_SECTIONS.section_timer);
		// 		if(evt.event === OVERLAY_EVENTS.start_timer) {
		// 			sendEventToIFrame(OVERLAY_EVENTS.make_hidden, EVENT_SECTIONS.exit_btn);
		// 		}
		// }

		// if (evt.event === OVERLAY_EVENTS.send_ticket_data) {
		// 	sendEventToIFrame(evt.event, EVENT_SECTIONS.section_timer);
		// }

		// if (evt.event === OVERLAY_EVENTS.elapsed_time) {
		// 	CAFE_FACTORY_TRACKING_DATA.timer = evt.arg;
		// 	setTimeout(() => {
		// 		endSimulation(CAFE_FACTORY_TRACKING_DATA);
		// 	}, 1000);
		// 	deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_elapsed_time, null, null,  CAFE_FACTORY_TRACKING_DATA.timer);
		// 	return;
		// }

		/**
		 * Manage events based on clicked overlay icon
		 * These events are supposed to initiate actions represented on
		 * the overlay buttons
		 */

		if (evt.event === OVERLAY_EVENTS.overlay_click) {
			// We now distinguish by the iframe this came from

			if (evt.section === EVENT_SECTIONS.cart_btn) {
				sendEventToIFrame(CART_EVENTS.open_cart, EVENT_SECTIONS.cart, CAFE_FACTORY_TRACKING_DATA);
				return;
			}
			if (evt.section === EVENT_SECTIONS.help_btn) {
				sendEventToIFrame(CART_EVENTS.open_help, EVENT_SECTIONS.cart);
				return;
			}
			if (evt.section === EVENT_SECTIONS.exit_btn) {
				// showBlackScreen();
				// setTimeout(() => {
				// 	endSimulation(CAFE_FACTORY_TRACKING_DATA);
				// }, 1000);
				if (activeMission.missionId === 1) {
					startSecondMission();
				} else {
					startFirstMission();
				}

				return;
			}

			return;
		}

		/**
		 * Manage events recived from the Product Card Popup (Product Section)
		 * These events are recived exclusively from the Product Section !!!
		 */

		if (evt.section === EVENT_SECTIONS.product) {

			// let TARGET_PRODUCT = evt.target;
			let TARGET_PRODUCT_ID = evt.arg.pid;
			let TARGET_PRODUCT_SKUID = evt.arg.skuid;
			let TARGET_PRODUCT_QTY = evt.arg.qty || null;
			let TARGET_PRODUCT_TYPE = evt.arg.type;
			let TARGET_PRODUCT_PRICE = evt.arg.price;

			if (evt.event === PRODUCT_EVENTS.open_product) {

				sendEventToIFrame(PRODUCT_EVENTS.send_product_data, EVENT_SECTIONS.product, CAFE_FACTORY_TRACKING_DATA);
				// Sent data: {custom_event_type: cafe_factory_product_open, target: SKUID, value: null, additional_data: null}
				deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_product_open, TARGET_PRODUCT_SKUID, null, null);

				activeProduct = {
					productId: TARGET_PRODUCT_ID,
					startedAt: new Date()
				};
			}
			else {
				const ELAPSED = activeProduct && activeProduct.productId === evt.arg.pid
					? new Date() - activeProduct.startedAt
					: 0;

				if (evt.event === PRODUCT_EVENTS.close_product) {

					// Sent data: {custom_event_type: cafe_factory_product_close, target: SKUID, value: ELAPSED_TIME, additional_data: null}
					deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_product_close, TARGET_PRODUCT_SKUID, ELAPSED, null);
				}
				else if (evt.event === PRODUCT_EVENTS.buy_product) {

					// Sent data: {custom_event_type: cafe_factory_product_buy, target: SKUID, value: ELAPSED_TIME, additional_data: QTY}
					deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_product_buy, TARGET_PRODUCT_SKUID, ELAPSED, JSON.stringify({TARGET_PRODUCT_QTY, TARGET_PRODUCT_TYPE}));

					CAFE_FACTORY_TRACKING_DATA.purchased_products[TARGET_PRODUCT_ID] = {
						skuId: TARGET_PRODUCT_SKUID,
						qty: TARGET_PRODUCT_QTY,
						type: TARGET_PRODUCT_TYPE,
						price: TARGET_PRODUCT_PRICE
					};
				}

			}

			return;
		}

		if (evt.section === EVENT_SECTIONS.cart) {


			if (evt.event === MISSION_EVENTS.open_card) {
				deps.jQuery('#loadingScreen').fadeOut(2000);
				sendEventToIFrame(MISSION_EVENTS.open_card, EVENT_SECTIONS.cart);
			}
			else if (evt.event === CART_EVENTS.remove_from_cart) {
				let TARGET_PRODUCT_SKUID = evt.arg;
				if (TARGET_PRODUCT_SKUID) {
					delete CAFE_FACTORY_TRACKING_DATA.purchased_products[TARGET_PRODUCT_SKUID];
				}

				// Sent data: {custom_event_type: cafe_factory_product_delete_from_cart, target: SKUID, value: null, additional_data: null}

				// JUST IN THIS CASE PID IS FOR 1 HIGHER THAN SKUID
				var skuid = TARGET_PRODUCT_SKUID - 1;
				deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_product_delete_from_cart, skuid, null, `pId:${TARGET_PRODUCT_SKUID}`);
				return;
			}
			else if (evt.event === CART_EVENTS.checkout_cart) {
				options.second_mission_first_pano
					? startSecondMission()
					: log.warn(`Missing "second_mission_first_pano" option. Can't start the second mission!`);
					CAFE_FACTORY_TRACKING_DATA.user = evt.arg.user;
					var total = 0;
					for(var product in CAFE_FACTORY_TRACKING_DATA.purchased_products){
						total = total + product.price;
					}

					var order = {
						user: CAFE_FACTORY_TRACKING_DATA.user,
						products: CAFE_FACTORY_TRACKING_DATA.purchased_products
					}

					var mailContent = mailTemplate(order);

					var params = {
						content: mailContent,
						// recipients: "dusan@spinview.io",
						// recipients: ["kat@spinview.io", "irena@cafe-factory.net"],
						recipients: ["info@cafe-factory.net"],
						subject: 'no-reply c&f'
					}

					deps.mailNotify.notify('notifications/send/generic', params);
					// Sent data: {custom_event_type: cafe_factory_product_buy, target: SKUID, value: ELAPSED_TIME, additional_data: QTY}
					deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_product_checkout, null, null, JSON.stringify(order));
					// CAFE_FACTORY_TRACKING_DATA.purchased_products = {};
					// CAFE_FACTORY_TRACKING_DATA.user = {};
			}
			else if (evt.event === MISSION_EVENTS.mission_start) {
				let CURRENT_MISSION = evt.arg;

				activeMission = {
					missionId: CURRENT_MISSION,
					startedAt: new Date()
				};

				if (CURRENT_MISSION >= 2) {
					CAFE_FACTORY_TRACKING_DATA.purchased_products = {};
					CAFE_FACTORY_TRACKING_DATA.user = {};
				}

				// Sent data: {custom_event_type: cafe_factory_mission_start, target: MISSION_ID, value: null, additional_data: null}
				deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_mission_start, CURRENT_MISSION, null, null);
			}
			else if (evt.event === MISSION_EVENTS.mission_stop) {
				let CURRENT_MISSION = evt.arg;

				const ELAPSED = activeMission && activeMission.missionId === CURRENT_MISSION
					? new Date() - activeMission.startedAt
					: 0;

				// Sent data: {custom_event_type: cafe_factory_mission_stop, target: MISSION_ID, value: ELAPSED_TIME, additional_data: null}
				deps.audienceTracker.sendCustomEvent(CUSTOM_EVENT_TYPES.cafe_factory_mission_stop, CURRENT_MISSION, ELAPSED, null);
			}
		}
	}


	/**
	 * Functions used to wrap some of event logic for better code readability
	 */

	function startSecondMission() {
		// activeMission.missionId++;
		deps.krPanoClient.goToScene(options.second_mission_first_pano);
		sendEventToIFrame(MISSION_EVENTS.next_mission, EVENT_SECTIONS.cart);
		sendEventToIFrame(OVERLAY_EVENTS.make_visible, EVENT_SECTIONS.cart_btn);
		// sendEventToIFrame(OVERLAY_EVENTS.make_hidden, EVENT_SECTIONS.section_timer);
	}

	function startFirstMission() {
		let urlParams = getUrlVars();
	    let currentPanoID = urlParams["s"] || null;
		let krPano = deps.krPanoClient.getKRPano();
		// let panos = krPano.data.getArray().filter(obj => obj.name.match(/en_pano\d+_title/));

		// let currentPano = panos.find(obj => obj.name === `en_${currentPanoID}_title`);
		// let movePano = panos.find(obj => obj.content === currentPano.content.substr(0, currentPano.content.indexOf('-')));

		// let movePanoId = movePano.name.replace('en_', '').replace('_title', '');

		const tourLanguageData = krPano.data.getArray();
		const panoTitlesHash = {};
		tourLanguageData.forEach(obj => {
			const matches = obj.name.match(/en_(pano\d+)_title/);
			if (!matches || !matches.length) {
				return null;
			}
			const panoId = matches[1];
			panoTitlesHash[panoId] = obj.content;
		});
		const currentPanoTitle = panoTitlesHash[currentPanoID];
		const currentSubStr = currentPanoTitle.slice(0, currentPanoTitle.indexOf('-'));
		const movePanoId = Object.keys(panoTitlesHash).find(key => panoTitlesHash[key] === currentSubStr)



		deps.krPanoClient.goToScene(movePanoId);
		// deps.krPanoClient.goToScene(options.first_mission_first_pano);
		sendEventToIFrame(MISSION_EVENTS.previous_mission, EVENT_SECTIONS.cart);
		sendEventToIFrame(OVERLAY_EVENTS.make_hidden, EVENT_SECTIONS.cart_btn);
		// sendEventToIFrame(OVERLAY_EVENTS.make_hidden, EVENT_SECTIONS.section_timer);
	}

	/**
	 * Function used to send CUSTOM events to children IFrames
	 * Usage: sendEventToIFrame(EVENT.event_name, EVENT_SECTIONS.section, Argument);
	 */

	function hideScroll(sections) {
		let styleNode = document.createElement('style');

		sections.forEach(section => {
			const webviewId = SECTION_TO_WEBVIEW[section];
			let style = "#"+webviewId+">.kolorBox>.kolorBoxBody>.kolorBoxBodyCenter {overflow: hidden !important;}";
			styleNode.innerHTML += style;
		});
		deps.jQuery('head').append(styleNode);

	}

	/**
	 * Function used to send CUSTOM events to children IFrames
	 * Usage: sendEventToIFrame(EVENT.event_name, EVENT_SECTIONS.section, Argument);
	 */

	function sendEventToIFrame(event, section, arg) {
		log.verbose('CAFE FACTORY CHILD FRAME EVENT', event, arg);

		/** @type CafeFactoryEvent */
		var evt = {};
		evt.receiver = RECEIVER;
		evt.event = event;
		evt.section = section;
		evt.arg = arg;

		const webviewId = SECTION_TO_WEBVIEW[section];
		if (!webviewId) {
			throw new Error(`Invalid section: ${section}`);
		}

		deps.jQuery('#kolorBoxID_' + webviewId)[0].contentWindow.postMessage(evt, '*');
	}

	/**
	 * Function used check if url changed
	 * If it did in this case check if we are on the last pano
	 * Then show exit button
	 */

	function checkLocation() {
	    if (currentPage != window.location.href)
	    {
	        currentPage = window.location.href;

	        let urlParams = getUrlVars();
	        let panoID = urlParams["s"] || null;

	        if (panoID === options.second_mission_last_pano) {
	        	sendEventToIFrame(OVERLAY_EVENTS.make_visible, EVENT_SECTIONS.exit_btn);
	        	clearInterval(_locationInterval);
	        }
	    }
	}

	/**
	 * Function used to show black GAMEOVER screen
	 * Made this way to avoid adding html directly inside tour as somone always forgets to do so
	 */

	function showBlackScreen() {
		var blackScreenContentStyle = 'style="display: flex; justify-content:center; min-height:100px; text-align: center; width: 300px; color: white; position: absolute; top: 50%; left: 50%; transform: translateX(-50%) translateY(-50%); box-sizing:border-box;"';
		var blackScreenContentAStyle = 'style="display: flex; justify-content:center; align-items: center; text-align: center; width: 250px; min-height:35px; color: black; background-color:white; position: absolute; top: 50%; left: 50%; transform: translateX(-50%) translateY(-50%); box-sizing:border-box; text-decoration:none;"';
		var blackScreenContent = '<p ' + blackScreenContentStyle + '>' + options.black_screen_text + '<br/><a href="javascript:history.go(0)"' + blackScreenContentAStyle + '>' + play_again + '</a></p>';

		var blackScreenStyle = 'style="width: 100%;height: 100%;position: absolute;top: 0;left: 0;background-color: black;z-index: 9999; font-family: "Arial", sans-serif;"';
		var blackScreen = '<div id="customStyleOverlayDarken" '+blackScreenStyle+'>'+blackScreenContent+'</div>';

		deps.jQuery('#panoDIV').prepend(blackScreen);
	}

	/**
	 * Function used to end simulation Session
	 * This function will end Audience tracker session and call a function
	 * that sends collected data back to GFK Simstore
	 * Usage: endSimulation(CollectedData);
	 */

	function endSimulation(cafe_factoryData) {
		deps.audienceTracker.endSession();

		if (cafe_factoryData) {
			sendToGfk(cafe_factoryData);
		}
	}

	/**
	 * Function used to send Collected Data back to GFK Simstore
	 * Usage: sendToGfk(CollectedData);
	 */

	function sendToGfk(cafe_factoryData) {
		if (!isInsideIFrame(deps.window)) {
			log.warn(`Failed to post the ${cafe_factoryData} message to the top window. We are not inside an iframe?`);
			return;
		}

		setTimeout(() => {
			deps.window.top.postMessage(cafe_factoryData, '*');
		}, 10);
	}

	/**
	 * Function used to get query parameters from url
	 * Usage: getUrlVars();
	 * Use it to detect panorama id
	 */

    function getUrlVars()
    {
        var vars = [], hash;
        var hashes = window.location.href.slice(window.location.href.indexOf('?') + 1).split('&');
        for(var i = 0; i < hashes.length; i++)
        {
            hash = hashes[i].split('=');
            vars.push(hash[0]);
            vars[hash[0]] = hash[1];
        }
        return vars;
    }

	/**
	 * Function used to send Warn about missing opitions properties
	 * Usage: warnMissingOptions(Options);
	 */

	function warnMissingOptions(options) {
		if (!options.first_mission_first_pano) {
		log.warn(`Missing "first_mission_first_pano" option. To set it up, add an additional script tag into the tour, formatted like this:

			<script type="text/javascript">
				window.SPINVIEW_OPTIONS = {
					CafeFactory: {
						first_mission_first_pano: 'my_pano_name'
					}
				};
			</script>
			`);
		}

		if (!options.second_mission_first_pano) {
			log.warn(`Missing "second_mission_first_pano" option. To set it up, add an additional script tag into the tour, formatted like this:

			<script type="text/javascript">
				window.SPINVIEW_OPTIONS = {
					CafeFactory: {
						second_mission_first_pano: 'my_pano_name'
					}
				};
			</script>
			`);
		}

		if (!options.second_mission_last_pano) {
			log.warn(`Missing "second_mission_last_pano" option. To set it up, add an additional script tag into the tour, formatted like this:

			<script type="text/javascript">
				window.SPINVIEW_OPTIONS = {
					CafeFactory: {
						second_mission_last_pano: 'my_pano_name'
					}
				};
			</script>
			`);
		}
	}
}

// *********************************************************************************************************************

/**
 * Defining Helper Functions
 * Use these to avoid writing repetative and tedious code
 */

// *********************************************************************************************************************

export default CafeFactory;

// Secret message: Na na na na na na na na na na na na na na na na... BATMAN!
